import * as React from 'react';
import { onIdTokenChanged, type User as FirebaseUser } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, firestore, functions } from '../lib/firebase';

export interface MembershipSnapshot {
  tier: 'trial' | 'free' | 'pro' | 'enterprise' | 'ultimate';
  status: 'active' | 'expired' | 'cancelled' | 'paused';
  validFrom?: { toDate(): Date } | Date | null;
  validUntil?: { toDate(): Date } | Date | null;
  notes?: string;
  planId?: string | null;
}

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'user';
  membership: MembershipSnapshot | null;
}

interface AuthContextValue {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
}

const AuthContext = React.createContext<AuthContextValue>({
  user: null,
  profile: null,
  loading: true,
  isAdmin: false,
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<FirebaseUser | null>(null);
  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const [loading, setLoading] = React.useState(true);
  const provisionedRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    return onIdTokenChanged(auth, async (u) => {
      setUser(u);
      setLoading(false);
      if (!u) {
        setProfile(null);
        return;
      }
      if (!provisionedRef.current.has(u.uid)) {
        provisionedRef.current.add(u.uid);
        try {
          const fn = httpsCallable<unknown, { ok: true; fresh: boolean; isAdmin: boolean }>(
            functions,
            'ensureUserProvisioned',
          );
          await fn({});
        } catch {
          /* best-effort */
        }
      }
    });
  }, []);

  // Live profile subscription so admin promotions / membership edits
  // surface immediately in the UI without a manual refresh.
  React.useEffect(() => {
    if (!user) return;
    const ref = doc(firestore, 'users', user.uid);
    return onSnapshot(
      ref,
      (snap) => {
        const data = snap.data();
        if (!data) {
          setProfile(null);
          return;
        }
        setProfile({
          uid: user.uid,
          email: (data.email as string) ?? user.email ?? '',
          displayName: (data.displayName as string) ?? user.displayName ?? '',
          firstName: (data.firstName as string) ?? '',
          lastName: (data.lastName as string) ?? '',
          role: (data.role as 'admin' | 'user') ?? 'user',
          membership: (data.membership as MembershipSnapshot | undefined) ?? null,
        });
      },
      () => {
        setProfile(null);
      },
    );
  }, [user]);

  const value: AuthContextValue = {
    user,
    profile,
    loading,
    isAdmin: profile?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useUser(): AuthContextValue {
  return React.useContext(AuthContext);
}
